import { BookEventSchema } from '@kitup/book-schema';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { ADMIN_OWNER_HEADER, adminOwnerIdFromPath, refreshSession } from '../api/client.js';
import { getAccessToken } from '../api/session-store.js';

const RECONNECT_MIN_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

/**
 * EventSource не умеет Authorization header, а access token у нас не лежит
 * в URL/cookie. Поэтому SSE читается fetch-stream: так сохраняются и безопасность,
 * и тихий refresh, и мгновенное закрытие потока при unmount.
 */
export function useBookEvents(bookId: string): void {
  const queryClient = useQueryClient();
  const connectionRef = useRef<{
    bookId: string;
    controller: AbortController;
    closeTimer?: number;
  } | null>(null);

  useEffect(() => {
    if (!bookId) return;
    let connection = connectionRef.current;
    if (connection?.closeTimer !== undefined) {
      window.clearTimeout(connection.closeTimer);
      connection.closeTimer = undefined;
    }
    if (!connection || connection.bookId !== bookId) {
      connection?.controller.abort();
      connection = { bookId, controller: new AbortController() };
      connectionRef.current = connection;
    } else {
      // React StrictMode повторно запустил эффект; поток уже работает.
      return scheduleClose(connection, connectionRef);
    }
    const controller = connection.controller;

    const invalidate = (): void => {
      void queryClient.invalidateQueries({ queryKey: ['book', bookId] });
      void queryClient.invalidateQueries({ queryKey: ['books'] });
      // Живой прогресс шагов «Герои», «Вещи» и «Локации» (E7): события генерации
      // приходят по каналу книги, а варианты лежат в составе книги.
      for (const key of ['book-characters', 'book-things', 'book-locations'] as const) {
        void queryClient.invalidateQueries({ queryKey: [key, bookId] });
      }
      void queryClient.invalidateQueries({ queryKey: ['story-drafts', bookId] });
      void queryClient.invalidateQueries({ queryKey: ['book-scenes', bookId] });
      void queryClient.invalidateQueries({ queryKey: ['book-illustrations', bookId] });
      void queryClient.invalidateQueries({ queryKey: ['book-looks', bookId] });
      void queryClient.invalidateQueries({ queryKey: ['book-cover', bookId] });
      void queryClient.invalidateQueries({ queryKey: ['book-exports', bookId] });
    };

    const run = async (): Promise<void> => {
      let reconnectMs = RECONNECT_MIN_MS;
      while (!controller.signal.aborted) {
        try {
          const token = getAccessToken();
          const adminOwnerId = adminOwnerIdFromPath(globalThis.location?.pathname ?? '');
          const response = await fetch(`/api/books/${encodeURIComponent(bookId)}/events`, {
            headers: {
              accept: 'text/event-stream',
              ...(token ? { authorization: `Bearer ${token}` } : {}),
              ...(adminOwnerId ? { [ADMIN_OWNER_HEADER]: adminOwnerId } : {}),
            },
            credentials: 'same-origin',
            signal: controller.signal,
          });
          if (response.status === 401) {
            if (!(await refreshSession())) await abortableDelay(reconnectMs, controller.signal);
            continue;
          }
          if (!response.ok || !response.body) throw new Error(`SSE HTTP ${response.status}`);
          reconnectMs = RECONNECT_MIN_MS;
          await readEventStream(response.body, invalidate, controller.signal);
          await abortableDelay(reconnectMs, controller.signal);
          reconnectMs = Math.min(reconnectMs * 2, RECONNECT_MAX_MS);
        } catch (error: unknown) {
          if (controller.signal.aborted || isAbortError(error)) return;
          await abortableDelay(reconnectMs, controller.signal).catch(() => undefined);
          reconnectMs = Math.min(reconnectMs * 2, RECONNECT_MAX_MS);
        }
      }
    };

    void run();
    return scheduleClose(connection, connectionRef);
  }, [bookId, queryClient]);
}

function scheduleClose(
  connection: { bookId: string; controller: AbortController; closeTimer?: number },
  connectionRef: {
    current: { bookId: string; controller: AbortController; closeTimer?: number } | null;
  },
): () => void {
  return () => {
    connection.closeTimer = window.setTimeout(() => {
      connection.controller.abort();
      if (connectionRef.current === connection) connectionRef.current = null;
    }, 0);
  };
}

async function readEventStream(
  stream: ReadableStream<Uint8Array>,
  onEvent: () => void,
  signal: AbortSignal,
): Promise<void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        consumeFrame(frame, onEvent);
        boundary = buffer.indexOf('\n\n');
      }
    }
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}

function consumeFrame(frame: string, onEvent: () => void): void {
  const data = frame
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart())
    .join('\n');
  if (!data) return;
  try {
    if (BookEventSchema.safeParse(JSON.parse(data) as unknown).success) onEvent();
  } catch {
    // Битый кадр не роняет долгоживущий поток.
  }
}

function abortableDelay(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timeout = window.setTimeout(resolve, milliseconds);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timeout);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}
